import { useState } from 'react'
import type { SyntheticEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useJournal } from '../api'
import { useLang } from '../lang'
import type { JournalDay } from '../types'
import { CalendarPicker } from './CalendarPicker'
import { NotableBlock } from './NotableBlock'
import { QuietButton, StateLine } from '../design'
import { unitOf } from '../time'
import * as j from './journal.css'

// Hide a plate whose image 404s — a scene not generated yet. The whole <figure> goes.
function hidePlate(e: SyntheticEvent<HTMLImageElement>) {
  const fig = e.currentTarget.closest('figure')
  if (fig) fig.style.display = 'none'
}

function csrf(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

// A zero-detection day's honest one-liner. We can't tell a quiet day from a dead mic, so we say
// how long the mic actually listened rather than assert silence. NEVER counts birds (there were none).
function quietLine(
  data: { offline: boolean; mic_hours: number | null },
  t: (en: string, ga: string) => string,
) {
  const h = data.mic_hours
  if (data.offline) {
    return h == null
      ? t('The station was offline this day.', 'Bhí an stáisiún as líne an lá seo.')
      : t(
          `The station was offline for much of this day — the mic recorded ${h} of 24 hours.`,
          `Bhí an stáisiún as líne ar feadh cuid mhór den lá seo — níor thaifead an micreafón ach ${h}/24 uair.`,
        )
  }
  return h == null
    ? t('A quiet day at the station.', 'Lá ciúin ag an stáisiún.')
    : t(
        `A quiet day — the mic listened ${h} of 24 hours and logged nothing.`,
        `Lá ciúin — bhí an micreafón ag éisteacht ${h}/24 uair agus níor thaifead sé faic.`,
      )
}

// The lead's kicker, keyed to what the day's composition says it is ABOUT.
const LEAD_KICKER: Record<string, [string, string]> = {
  bird: ['The day’s bird', 'Éan an lae'],
  weather: ['The weather', 'An aimsir'],
  quiet: ['A quiet day', 'Lá ciúin'],
  offline: ['Off the air', 'As líne'],
  place: ['A local story', 'Scéal áitiúil'],
  feast: ['The feast', 'An fhéile'],
  abundance: ['A full day', 'Lá gnóthach'],
}

// How many of the day's birds the rail shows before it offers the rest. A display choice, not a
// payload cap — /api/journal carries every species, so expanding needs no second fetch.
const RAIL_VISIBLE = 12

// A composed shape arrives as one string carrying its own second rank of type: "A Dunlin on the
// shore — a rare visitor to Culfin". Printed, that is a headline and a deck, not a sentence. Split
// at the first spaced em/en dash and set them as two ranks. Both halves must be substantial, so a
// dash used mid-phrase doesn't produce a one-word deck; anything else is left as a single headline.
function splitHeadline(text: string): [string, string | null] {
  const m = text.match(/^(.+?)\s+[—–]\s+(.+)$/)
  if (!m) return [text, null]
  const [, head, deck] = m
  if (head.trim().length < 10 || deck.trim().length < 10) return [text, null]
  return [head.trim(), deck.trim()]
}

const COMPASS8 = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
function compass(deg: number | null | undefined): string | null {
  if (deg == null) return null
  return COMPASS8[Math.round((((deg % 360) + 360) % 360) / 45) % 8]
}

// A retrieved dúchas story is normally from the parish, and the placename says enough. A last-resort
// wider/distant one gets a locator, so it is never passed off as local (CLAUDE.md §4).
const ZONE_LOCATOR: Record<string, [string, string]> = {
  wider: ['north-west Connemara', 'iarthuaisceart Chonamara'],
  distant: ['further afield', 'níos faide ó bhaile'],
}

// The LORE's mark: the PLACE reduced to a line vignette (PlaceVignette), opening a lore block. Every
// picture on this page used to be of a bird or of the whole day, so a story about Lettergesh had
// nothing of Lettergesh beside it. Decorative, so it is hidden from the reader of a screen reader —
// the place is named in the words directly below. Hidden entirely until the mark has been rendered.
function PlaceMark({ src }: { src: string | null }) {
  if (!src) return null
  return (
    <figure className={j.placeMark}>
      <img src={src} alt="" aria-hidden="true" loading="lazy" onError={hidePlate} />
    </figure>
  )
}

type Remembered = NonNullable<JournalDay['remembered']>

// A REMEMBERED bird — rare, lost or extinct, kept in verse rather than heard. Headed by its status,
// never by the day, so it never reads as part of the day's log. The rubric is English only until a
// fluent reader supplies the Irish (CLAUDE.md §0.3); Irish text arrives only once verified.
function RememberedBird({ bird, onSelect }: { bird: Remembered; onSelect: (sci: string) => void }) {
  const { lang } = useLang()
  const body = lang === 'ga' && bird.text_ga ? bird.text_ga : bird.text
  return (
    <section className={j.band}>
      <p className={j.rubric}>Remembered · {bird.heading}</p>
      <figure className={j.loreItem}>
        {bird.title && <p className={j.sectionTitle}>{bird.title}</p>}
        {/* A poem keeps its lines (the verse treatment); prose may reflow into columns. */}
        {body && (
          <blockquote className={bird.kind === 'poem' ? j.verse : `${j.prose} ${j.bandCols}`}>
            {body}
          </blockquote>
        )}
        {bird.quote && <blockquote className={j.verse}>{bird.quote}</blockquote>}
        <figcaption className={j.dlink}>
          <button type="button" className={j.recBird} onClick={() => onSelect(bird.sci)}>
            {lang === 'ga' && bird.ga ? bird.ga : bird.en}
          </button>
        </figcaption>
        {bird.note && <p className={j.credit}>{bird.note}</p>}
        {bird.credit && <p className={j.credit}>{bird.credit}</p>}
      </figure>
    </section>
  )
}

type DuchasStory = NonNullable<JournalDay['place_duchas']>

// THE DÚCHAS STORY — a Schools' Collection tale for a place in the parish, © the National Folklore
// Collection under CC BY-NC, so the credit is not decoration. On an ordinary day it stands on its
// own; on a silent one it is the aside beside the curated story, which is what `className` marks.
function DuchasPlace({ story, className }: { story: DuchasStory; className?: string }) {
  const { t, lang } = useLang()
  const text = lang === 'ga' && story.text_ga ? story.text_ga : story.text
  if (!text) return null

  const locator = story.zone ? ZONE_LOCATOR[story.zone] : undefined
  const src = story.source
  const body = (
    <>
      <PlaceMark src={story.vignette} />
      <p className={j.rubric}>
        {story.place}
        {locator && ` · ${t(locator[0], locator[1])}`}
      </p>
      <p className={`${j.prose} ${className ? '' : j.bandCols}`}>{text}</p>
      {src?.holder && (
        <p className={j.credit}>
          {src.holder}
          {src.licence && ' · '}
          {src.licence &&
            (src.licence_url ? (
              <a href={src.licence_url} target="_blank" rel="noopener noreferrer">
                {src.licence}
              </a>
            ) : (
              src.licence
            ))}
          {src.url && ' · '}
          {src.url && (
            <a href={src.url} target="_blank" rel="noopener noreferrer">
              dúchas.ie ↗
            </a>
          )}
        </p>
      )}
      {!src?.holder && src?.url && (
        <p className={j.credit}>
          <a href={src.url} target="_blank" rel="noopener noreferrer">
            dúchas.ie ↗
          </a>
        </p>
      )}
    </>
  )
  return className ? <aside className={className}>{body}</aside> : <div>{body}</div>
}

// THE JOURNAL — a broadsheet FRONT PAGE for one day. Not a stack of image → image → text: the day's
// elements are composed into one page — a lead splash against a ruled record rail, a band of the day's
// detections, the old calendar, the lore of its places — emphasis following the day's shape (DayComposer).
// Every line traces to real data or an attributed source; nothing invented, and DETECTIONS are never
// spoken of as individual birds.
export function JournalTab({
  onSelect,
  isAdmin,
  date,
  onDate,
}: {
  onSelect: (sci: string) => void
  isAdmin: boolean
  date: string | null
  onDate: (date: string | null) => void
}) {
  const { data, isLoading, isError } = useJournal(date)
  const { t, lang } = useLang()
  const qc = useQueryClient()
  const [regenBusy, setRegenBusy] = useState(false)
  const [railExpanded, setRailExpanded] = useState(false)
  // Which lead pictures 404'd, and on which day — see the lead derivation below.
  const [failedLead, setFailedLead] = useState<{ date: string | null; srcs: string[] }>({
    date: null,
    srcs: [],
  })

  const regenerate = () => {
    if (!data?.date || regenBusy) return
    setRegenBusy(true)
    fetch('/admin/journal/regenerate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf() },
      body: JSON.stringify({ date: data.date }),
    })
      .then(() => qc.invalidateQueries({ queryKey: ['journal'] }))
      .catch(() => undefined)
      .finally(() => setRegenBusy(false))
  }

  if (isLoading || !data) {
    return <StateLine busy={!isError}>{isError ? '—' : '…'}</StateLine>
  }

  const pick = (b: { en: string; ga: string }) => (lang === 'ga' ? b.ga : b.en)
  const bullets = lang === 'ga' ? data.summary.ga : data.summary.en
  const f = data.figures
  const busiest = f.busiest
  const hero = data.hero
  const hasBirds = bullets.length > 0 && data.source !== 'template'

  // ---- The lead picture -------------------------------------------------------
  //
  // A DAY WITH A BIRD SHOWS THE BIRD. That is the rule, and it is the rule because it is what a
  // bird-listening station's journal is for; the day's composed scene is the picture for a day
  // that has no bird to show.
  //
  // It ran the other way round for a long time, and the code argued for it in five places at once
  // — the composition prompt, JournalScene, GenerateJournalSceneJob, dayscenegen.py and this
  // component, each asserting the journal's picture is scenery and never a bird, and this one
  // preferring the scene with the hero's own plate as the fallback for a scene that hadn't
  // rendered. A landscape led every single day, captioned with the name of a bird not in it.
  //
  // In order, then: the hero's own plate; a credited photo of the hero, for the birds the station
  // ships no art for; the day's composed scene; the place's picture on a birdless day. Both bird
  // candidates come BEFORE the scene, because "we have no linocut of this one" is not a reason to
  // show a landscape instead — a rare visitor is the day you least want one. Each 404 steps to the
  // next, and the caption follows whichever picture is showing: the bird's name under the bird, the
  // ground's name under the ground, and never the one under the other.
  const composedImagery = data.composition?.imagery ?? []
  const sceneUrl = (i: number) => `/journal-scene/${data.date}${i === 0 ? '' : `-${i + 1}`}.png`
  const composedScene = composedImagery.length > 0 ? sceneUrl(0) : null
  const scenePlace = composedImagery[0]?.place ?? data.place?.place ?? data.place_duchas?.place ?? null
  const leads: { src: string; bird: boolean; place: string | null; credit?: string }[] = [
    ...(hasBirds && hero?.illustration ? [{ src: hero.illustration, bird: true, place: null }] : []),
    ...(hasBirds && hero?.photo
      ? [{ src: hero.photo.url, bird: true, place: null, credit: hero.photo.credit }]
      : []),
    ...(composedScene ? [{ src: composedScene, bird: false, place: scenePlace }] : []),
    ...(!hasBirds && data.place?.image
      ? [{ src: data.place.image, bird: false, place: data.place.place }]
      : []),
  ]
  // Which sources 404'd, kept against the day they failed on so moving to another day clears
  // them by itself. Through React rather than the DOM, so the caption hears about the step down.
  const failedSrcs = failedLead.date === data.date ? failedLead.srcs : []
  const lead = leads.find((candidate) => !failedSrcs.includes(candidate.src)) ?? null
  const leadSrc = lead?.src ?? null
  const leadIsBird = !!lead?.bird
  const leadPlace = lead?.place ?? null
  // A CC photo may only be shown with its credit, so the credit rides with the picture it belongs
  // to and disappears the moment the lead steps down to another one.
  const leadCredit = lead?.credit ?? null
  const leadCaptioned = (leadIsBird && !!hero) || !!leadPlace

  // Birds first. A day WITH birds leads on the composed headline (birds-first by the composer), or the
  // hero bird. A birdless day leads honestly on the STATION itself — the quiet, or being off the air —
  // NEVER on a place name or any stand-in for one (place lore is a section lower down, never the
  // headline).
  const leadKind = data.composition?.lead?.kind
  const eyebrow = !hasBirds
    ? t('At the station', 'Ag an stáisiún')
    : (() => {
        const k = (leadKind && LEAD_KICKER[leadKind]) || LEAD_KICKER.bird
        return lang === 'ga' ? k[1] : k[0]
      })()
  const [headline, deck] = !hasBirds
    ? ([data.offline ? t('Off the air', 'As líne') : t('A quiet day', 'Lá ciúin'), null] as [
        string,
        string | null,
      ])
    : splitHeadline(data.composition?.shape || (hero ? hero.en : pick(data.date_label)))
  // A front page never sets the same words twice running. The composer often opens the day's shape
  // with the exact phrase the kicker carries — "A QUIET DAY" over "A quiet day — the Magpies barely
  // called" — and a kicker that only repeats the headline is worse than no kicker at all.
  const showKicker = !headline.toLowerCase().startsWith(eyebrow.toLowerCase())
  const listened = data.mic_hours == null ? null : `${data.mic_hours}/${unitOf(24, 'h')}`
  const dayBirds = [...data.heard].sort((a, b) => b.count - a.count || a.en.localeCompare(b.en))
  // The rail shows a dozen and offers the rest — a display choice now, not a truncated payload.
  const railBirds = railExpanded ? dayBirds : dayBirds.slice(0, RAIL_VISIBLE)

  const dl = data.day_lore
  const saints = dl?.narrative?.saints ?? []
  const feastName = dl?.title ? pick(dl.title) : saints.length ? saints.join(', ') : null
  const feastGloss = dl?.gloss ? pick(dl.gloss) : null
  const feastSource =
    dl?.narrative?.credit_url ??
    (Array.isArray(dl?.sources) && dl?.sources.length ? dl!.sources[0].url : null)
  const feastVerse = dl?.narrative?.verse ? dl?.narrative?.text : null
  const calendarText = feastGloss ?? (dl?.narrative && !dl.narrative.verse ? dl.narrative.text : null)

  const onProseClick = (e: SyntheticEvent<HTMLDivElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-sci]')
    if (el?.dataset.sci) onSelect(el.dataset.sci)
  }

  // A missing picture steps down one rung — the bird, then the day's scene, then nothing at all.
  // Recording WHICH src failed is what lets the caption follow it down.
  const onLeadError = () => leadSrc && setFailedLead({ date: data.date, srcs: [...failedSrcs, leadSrc] })

  const spark = data.sparkline

  return (
    <section>
      {data.date ? (
        <article className={j.front}>
          {/* Dateline — a proper edition marker: admin redraw · the big date · listening state. */}
          <div className={j.dateline}>
            <span className={j.datelineSide}>
              {isAdmin && (
                <button
                  type="button"
                  className={j.regen}
                  onClick={regenerate}
                  disabled={regenBusy}
                  title={t('Regenerate this day', 'Athghin an lá seo')}
                  aria-label={t('Regenerate this day', 'Athghin an lá seo')}
                >
                  <i className={`ti ${regenBusy ? 'ti-loader' : 'ti-feather'}`} aria-hidden="true" />
                </button>
              )}
            </span>
            <CalendarPicker
              date={data.date}
              dateLabel={pick(data.date_label)}
              available={data.available}
              onChange={onDate}
            />
            <span className={j.datelineRight}>
              {data.offline
                ? t('Offline', 'As líne')
                : listened
                  ? t(`Listening ${listened}`, `Ag éisteacht ${listened}`)
                  : ''}
            </span>
          </div>

          {/* THE COMPOSED GRID — lead splash against the record + calendar rail. */}
          <div className={j.grid}>
            <main className={j.leadCol}>
              {/* Headline before the picture. A front page leads on words and illustrates them; a
                  picture on top of the headline is a blog post's hero banner, which is the one
                  thing this page must not read as. */}
              <header className={j.leadHead}>
                {showKicker && <p className={j.kicker}>{eyebrow}</p>}
                <h2 className={j.headline}>{headline}</h2>
                {deck && <p className={j.deck}>{deck}</p>}
              </header>
              {leadSrc && (
                <figure className={j.leadFig}>
                  {/* A <figure> associates the caption below with the picture, so where there IS
                      a caption the image takes no alt rather than announcing the same words
                      twice; a picture nothing names keeps a real one. */}
                  <img
                    src={leadSrc}
                    alt={leadCaptioned ? '' : pick(data.date_label)}
                    loading="eager"
                    onError={onLeadError}
                  />
                  {leadIsBird && hero ? (
                    <figcaption className={j.leadCaption}>
                      <button type="button" className={j.leadName} onClick={() => onSelect(hero.sci)}>
                        {lang === 'ga' && hero.ga ? hero.ga : hero.en}
                      </button>{' '}
                      <span className={j.leadSci}>{hero.sci}</span>
                      {leadCredit && <span className={j.leadCredit}>{leadCredit}</span>}
                    </figcaption>
                  ) : leadPlace ? (
                    <figcaption className={j.leadCaption}>{leadPlace}</figcaption>
                  ) : null}
                </figure>
              )}
              {data.note && <p className={j.note}>{data.note}</p>}
              {hasBirds ? (
                <div className={j.body} onClick={onProseClick}>
                  {bullets.map((html, i) => (
                    <p
                      key={i}
                      className={i === 0 ? j.drop : undefined}
                      dangerouslySetInnerHTML={{ __html: html }}
                    />
                  ))}
                </div>
              ) : (
                <p className={j.standfirst}>{quietLine(data, t)}</p>
              )}
              {/* A tailpiece: a small line vignette of the day's bird in the station's own hand
                  (Vignette), closing the lead like an old printer's mark. Hidden until generated
                  (onError) and only on a day that actually has a hero bird.
                  NOT when the lead picture is that same catalogue plate, though: the vignette is a
                  line REDUCTION of /birds/<slug>.png, so beneath that very drawing it reads as a
                  copy of the picture above it rather than a mark. It earns its place under any other
                  lead — a Wikimedia photo, say, where it is the one thing in the station's own
                  hand. The LORE has its own marks now (PlaceMark). */}
              {hasBirds && hero?.vignette && leadSrc !== hero.illustration && (
                <figure className={j.tailpiece}>
                  <img src={hero.vignette} alt="" aria-hidden="true" loading="lazy" onError={hidePlate} />
                </figure>
              )}
            </main>

            <aside className={j.rail}>
              {/* The station record — an editorial strip, not KPI cards (brief §5.7). */}
              <section aria-label={t('The record', 'An taifead')}>
                <h3 className={j.railHead}>{t('The record', 'An taifead')}</h3>
                <dl className={j.record}>
                  <div>
                    <dt>
                      <i className={`ti ti-feather ${j.recIcon}`} aria-hidden="true" />
                      {t('Species', 'Speicis')}
                    </dt>
                    <dd>{f.species.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt>
                      <i className={`ti ti-wave-sine ${j.recIcon}`} aria-hidden="true" />
                      {t('Detections', 'Brathanna')}
                    </dt>
                    <dd>{f.detections.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt>
                      <i className={`ti ti-clock ${j.recIcon}`} aria-hidden="true" />
                      {t('Listened', 'Éisteacht')}
                    </dt>
                    <dd>{listened ?? '—'}</dd>
                  </div>
                  {busiest && (
                    <div>
                      <dt>
                        <i className={`ti ti-microphone ${j.recIcon}`} aria-hidden="true" />
                        {t('Most detected', 'Ba mhinice')}
                      </dt>
                      <dd className={j.recNameFig}>
                        <button type="button" className={j.recBird} onClick={() => onSelect(busiest.sci)}>
                          {lang === 'ga' && busiest.ga ? busiest.ga : busiest.en}
                        </button>
                        <span>{busiest.count.toLocaleString()}</span>
                      </dd>
                    </div>
                  )}
                  {data.weather?.text && (
                    <div>
                      {/* The day's own sky glyph sits in the label column with every other row's
                          icon. It used to ride on the VALUE side, so Conditions was the one row
                          in the strip with an empty icon column and a doubled-up value. */}
                      <dt>
                        <i
                          className={`ti ${data.weather.icon ?? 'ti-cloud'} ${j.recIcon}`}
                          aria-hidden="true"
                        />
                        {t('Conditions', 'Aimsir')}
                      </dt>
                      <dd className={j.recWord}>{data.weather.text}</dd>
                    </div>
                  )}
                  {data.weather?.wind_kmh != null && (
                    <div>
                      <dt>
                        <i className={`ti ti-wind ${j.recIcon}`} aria-hidden="true" />
                        {t('Wind', 'Gaoth')}
                      </dt>
                      <dd>
                        {compass(data.weather.wind_deg) ? `${compass(data.weather.wind_deg)} ` : ''}
                        {Math.round(data.weather.wind_kmh)} <small className={j.unit}>km/h</small>
                      </dd>
                    </div>
                  )}
                  {(data.weather?.temp_min != null || data.weather?.temp_max != null) && (
                    <div>
                      <dt>
                        <i className={`ti ti-temperature ${j.recIcon}`} aria-hidden="true" />
                        {t('Temperature', 'Teocht')}
                      </dt>
                      <dd>
                        {data.weather.temp_min != null &&
                        data.weather.temp_max != null &&
                        data.weather.temp_min !== data.weather.temp_max
                          ? `${data.weather.temp_min}–${data.weather.temp_max}`
                          : (data.weather.temp_max ?? data.weather.temp_min)}{' '}
                        <small className={j.unit}>°C</small>
                      </dd>
                    </div>
                  )}
                  {data.weather && data.weather.precip_mm != null && data.weather.precip_mm >= 0.1 && (
                    <div>
                      <dt>
                        <i className={`ti ti-droplets ${j.recIcon}`} aria-hidden="true" />
                        {t('Rain', 'Báisteach')}
                      </dt>
                      <dd>
                        {Math.round(data.weather.precip_mm * 10) / 10} <small className={j.unit}>mm</small>
                      </dd>
                    </div>
                  )}
                </dl>
                {spark && spark.path && (
                  <figure className={j.spark}>
                    <figcaption>{t('The day’s hours', 'Uaireanta an lae')}</figcaption>
                    <svg viewBox={`0 0 ${spark.w} ${spark.h}`} preserveAspectRatio="none" aria-hidden="true">
                      {spark.fill && <path d={spark.fill} className="fill" />}
                      <path d={spark.path} className="line" />
                    </svg>
                  </figure>
                )}
              </section>

              {data.heard.length > 0 && (
                <section aria-label={t('The day in birds', 'Éin an lae')}>
                  <h3 className={j.railHead}>{t('The day in birds', 'Éin an lae')}</h3>
                  <ul className={j.railList}>
                    {railBirds.map((b) => (
                      <li key={b.sci}>
                        <button type="button" className={j.railBird} onClick={() => onSelect(b.sci)}>
                          <span className={j.railBirdName}>{lang === 'ga' && b.ga ? b.ga : b.en}</span>
                          <span className={j.railBirdCount}>{b.count.toLocaleString()}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  {dayBirds.length > RAIL_VISIBLE && (
                    <QuietButton
                      className={j.railMore}
                      aria-expanded={railExpanded}
                      onClick={() => setRailExpanded((open) => !open)}
                    >
                      {railExpanded
                        ? t('Show fewer', 'Níos lú')
                        : t(
                            `+ ${dayBirds.length - RAIL_VISIBLE} more species`,
                            `+ ${dayBirds.length - RAIL_VISIBLE} speiceas eile`,
                          )}
                    </QuietButton>
                  )}
                </section>
              )}

              {(feastName || calendarText || feastVerse) && (
                <section aria-label={t('On the old calendar', 'Ar an sean-fhéilire')}>
                  <h3 className={j.railHead}>{t('On the old calendar', 'Ar an sean-fhéilire')}</h3>
                  {feastName && <p className={j.sectionTitle}>{feastName}</p>}
                  {feastVerse && <blockquote className={j.verse}>{feastVerse}</blockquote>}
                  {calendarText && <p className={j.prose}>{calendarText}</p>}
                  {(dl?.narrative?.credit || feastSource) && (
                    <p className={j.credit}>
                      {feastSource ? (
                        <a href={feastSource} target="_blank" rel="noopener noreferrer">
                          {dl?.narrative?.credit_label ?? t('source', 'foinse')} ↗
                        </a>
                      ) : (
                        dl?.narrative?.credit
                      )}
                    </p>
                  )}
                </section>
              )}
            </aside>
          </div>

          {/* IN ART & ARCHAEOLOGY — the hero bird as a cited plate (bird_art.yml, verified only). */}
          {data.art && (
            <section className={j.band} aria-label={t('Art & archaeology', 'Ealaín is seandálaíocht')}>
              <p className={j.rubric}>{t('Art & archaeology', 'Ealaín is seandálaíocht')}</p>
              <div className={j.art}>
                <figure className={j.artPlate}>
                  <img
                    src={data.art.image}
                    alt={data.art.title ?? data.art.en}
                    loading="lazy"
                    onError={hidePlate}
                  />
                </figure>
                <div>
                  {data.art.title && (
                    <h3 className={j.sectionTitle}>
                      {data.art.title}
                      {data.art.culture && <span className={j.artCulture}> · {data.art.culture}</span>}
                    </h3>
                  )}
                  {data.art.fragment && <p className={j.prose}>{data.art.fragment}</p>}
                  <p className={j.artTreatment}>
                    {data.art.treatment === 'display'
                      ? t(
                          `Shown as held by ${data.art.holder ?? 'the collection'}`,
                          `Ar taispeáint mar atá sé i ${data.art.holder ?? 'an bhailiúchán'}`,
                        )
                      : t(
                          'Restyled in the station’s linocut hand, after the original',
                          'Athstílocha i lámh línó an stáisiúin, i ndiaidh an bhunsaothair',
                        )}
                  </p>
                  {(data.art.credit.attribution || data.art.credit.url) && (
                    <p className={j.credit}>
                      {data.art.credit.url ? (
                        <a href={data.art.credit.url} target="_blank" rel="noopener noreferrer">
                          {data.art.credit.attribution ?? data.art.holder ?? t('source', 'foinse')} ↗
                        </a>
                      ) : (
                        data.art.credit.attribution
                      )}
                      {data.art.credit.licence &&
                        (data.art.credit.licence_url ? (
                          <>
                            {' · '}
                            <a
                              href={data.art.credit.licence_url}
                              target="_blank"
                              rel="license noopener noreferrer"
                            >
                              {data.art.credit.licence}
                            </a>
                          </>
                        ) : (
                          ` · ${data.art.credit.licence}`
                        ))}
                    </p>
                  )}
                  {/* Not decoration, and not optional: CC BY 4.0 §3(a)(1)(B) makes indicating a
                      modification a condition of the licence, and a linocut redraw is one. The
                      server-rendered citation panel has carried this from the start; the Journal
                      is the surface readers actually see, so it carries it too. */}
                  {data.art.credit.adaptation_note && (
                    <p className={j.credit}>{data.art.credit.adaptation_note}</p>
                  )}
                  {/* derived_note + output_licence come as a pair from BirdArtCitation, which owns
                      the wording ("inherits the source's terms" vs "an original work of the
                      station"). The panel used to compose its own sentence here and could say the
                      wrong one — the whole point of a single citation builder is that it doesn't. */}
                  {(data.art.credit.derived_note || data.art.credit.output_licence) && (
                    <p className={j.credit}>
                      {data.art.credit.derived_note}
                      {data.art.credit.output_licence &&
                        (data.art.credit.output_licence_url ? (
                          <>
                            {' '}
                            <a
                              href={data.art.credit.output_licence_url}
                              target="_blank"
                              rel="license noopener noreferrer"
                            >
                              {data.art.credit.output_licence}
                            </a>
                          </>
                        ) : (
                          ` ${data.art.credit.output_licence}`
                        ))}
                    </p>
                  )}
                  {data.art.credit.nc_restricted && (
                    <p className={j.credit}>
                      {t(
                        'Non-commercial — excluded from any paid context.',
                        'Neamhthráchtála — fágtha as aon chomhthéacs íoctha.',
                      )}
                    </p>
                  )}
                </div>
              </div>
            </section>
          )}

          <NotableBlock groups={data.notable} onSelect={onSelect} />

          {/* PLACE LORE. The CURATED story carries a silent day (`place` is null on a day
              with birds); the DÚCHAS story stands on its own on any day the calendar has nothing. */}
          {(data.place || data.place_duchas) && (
            <section className={j.band}>
              {data.place ? (
                <div className={j.placeLore}>
                  <div>
                    <PlaceMark src={data.place.vignette} />
                    <p className={j.rubric}>{data.place.place}</p>
                    {data.place.title && <h3 className={j.sectionTitle}>{data.place.title}</h3>}
                    {data.place.text && <p className={`${j.prose} ${j.bandCols}`}>{data.place.text}</p>}
                    {data.place.quote && <blockquote className={j.verse}>{data.place.quote}</blockquote>}
                    {data.place.credit && <p className={j.credit}>{data.place.credit}</p>}
                  </div>
                  {data.place_duchas && (
                    <DuchasPlace story={data.place_duchas} className={j.placeLoreAside} />
                  )}
                </div>
              ) : (
                data.place_duchas && <DuchasPlace story={data.place_duchas} />
              )}
            </section>
          )}

          {/* The day's closing bird lore — attributed quote(s), set apart, with credit/link. */}
          {data.quotes?.length ? (
            <section className={j.band}>
              <p className={j.rubric}>{t('Lore & Wisdom', 'Béaloideas is eagna')}</p>
              {data.quotes.map((q) => {
                const body = lang === 'ga' && q.text_ga ? q.text_ga : q.text
                const url = q.source?.url
                return (
                  <figure key={`${q.sci}-${q.kind}`} className={j.loreItem}>
                    {q.title && <p className={j.sectionTitle}>{q.title}</p>}
                    {body && (
                      <blockquote className={q.kind === 'poem' ? j.verse : `${j.prose} ${j.bandCols}`}>
                        {body}
                      </blockquote>
                    )}
                    {q.quote && <blockquote className={j.verse}>{q.quote}</blockquote>}
                    <figcaption className={j.dlink}>
                      <button type="button" className={j.recBird} onClick={() => onSelect(q.sci)}>
                        {lang === 'ga' && q.ga ? q.ga : q.en}
                      </button>
                      {url && (
                        <>
                          {' · '}
                          <a href={url} target="_blank" rel="noopener noreferrer">
                            dúchas.ie ↗
                          </a>
                        </>
                      )}
                    </figcaption>
                    {q.credit && <p className={j.credit}>{q.credit}</p>}
                  </figure>
                )
              })}
            </section>
          ) : null}

          {/* A REMEMBERED bird — rare, lost or extinct, kept in verse rather than heard. Headed by
              its status, never by the day, so it never reads as part of the day's log. */}
          {data.remembered && <RememberedBird bird={data.remembered} onSelect={onSelect} />}
        </article>
      ) : (
        <StateLine>
          {t('No completed days yet — come back tomorrow.', 'Gan lá iomlán fós — fill amárach.')}
        </StateLine>
      )}
    </section>
  )
}
